/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Title } from 'app/components/atoms/title/title';
import moment from 'moment';
import { useBackup } from 'app/hooks/requests/settings/use-backup';
import { Alert, AlertDescription, AlertTitle } from 'app/components/ui/alert';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { RestoreResult, useRestore } from 'app/hooks/requests/settings/use-restore';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { AlertTriangle, CheckCircle2, Download, FileUp, RotateCcw } from 'lucide-react';

/**
 * `Section` component.
 */

function Section({
  icon,
  title,
  description,
  children
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
      <div className={'flex items-start gap-3'}>
        <span
          className={
            'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary [&_svg]:h-5 [&_svg]:w-5'
          }
        >
          {icon}
        </span>

        <div className={'min-w-0'}>
          <h3 className={'text-sm font-semibold text-foreground'}>{title}</h3>

          <p className={'mt-0.5 text-sm text-muted-foreground'}>{description}</p>
        </div>
      </div>

      <div className={'mt-4'}>{children}</div>
    </section>
  );
}

/**
 * Format a file size for display.
 */

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Export `SettingsBackups` component.
 */

export function SettingsBackups(): JSX.Element {
  const [translate] = useTranslation();
  const { mutateAsync: backup, isLoading: isBackingUp } = useBackup();
  const { mutateAsync: restore, isLoading: isRestoring } = useRestore();
  const user = useMemo(() => cookieStorage.get('user') as any, []);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<RestoreResult | null>(null);

  const handleBackup = useCallback(() => {
    backup().then((data: any) => {
      const blob = new Blob([data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `backup_${moment().toISOString()}.sql`;
      link.click();
    });
  }, [backup]);

  const handlePickFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setResult(null);
  }, []);

  const handleRestore = useCallback(async () => {
    if (!file) {
      return;
    }

    try {
      const data = await restore(file);

      setResult(data);
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success({
        message: translate('settings.backups.restore.success'),
        description: translate('settings.backups.restore.migrationsApplied', { count: data.migrations.length })
      });
    } catch (error) {
      const detail = (error as any)?.response?.data?.data;

      toast.error({
        message: translate('settings.backups.restore.failed'),
        description: typeof detail === 'string' ? detail : undefined
      });
    }
  }, [file, restore, toast, translate]);

  if (user?.id !== 1) {
    return (
      <div className={'max-w-[640px] space-y-5'}>
        <Title>{translate('settings.backups.title')}</Title>

        <div
          className={
            'flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-5 py-6 text-sm text-muted-foreground'
          }
        >
          <AlertTriangle className={'h-5 w-5 shrink-0'} />

          {translate('settings.backups.notAllowed')}
        </div>
      </div>
    );
  }

  return (
    <div className={'max-w-[640px] space-y-5'}>
      <div>
        <Title>{translate('settings.backups.title')}</Title>

        <p className={'mt-1 text-sm text-muted-foreground'}>{translate('settings.backups.description')}</p>
      </div>

      <Section
        description={translate('settings.backups.backup.description')}
        icon={<Download />}
        title={translate('settings.backups.backup.title')}
      >
        <Button isLoading={isBackingUp} onClick={handleBackup} type={'primary'}>
          {translate('settings.backups.backup.action')}
        </Button>
      </Section>

      <Section
        description={translate('settings.backups.restore.description')}
        icon={<RotateCcw />}
        title={translate('settings.backups.restore.title')}
      >
        <Alert variant={'destructive'}>
          <AlertTriangle className={'h-4 w-4'} />

          <AlertTitle>{translate('settings.backups.restore.warningTitle')}</AlertTitle>

          <AlertDescription>{translate('settings.backups.restore.warning')}</AlertDescription>
        </Alert>

        <input
          accept={'.sql,application/sql,text/plain'}
          className={'hidden'}
          onChange={handlePickFile}
          ref={fileInputRef}
          type={'file'}
        />

        <div className={'mt-4 flex flex-wrap items-center gap-3'}>
          <Button disabled={isRestoring} onClick={() => fileInputRef.current?.click()}>
            <FileUp className={'h-4 w-4'} />

            {translate('settings.backups.restore.chooseFile')}
          </Button>

          <span className={'min-w-0 truncate text-sm text-muted-foreground'}>
            {file ? `${file.name} (${formatSize(file.size)})` : translate('settings.backups.restore.noFile')}
          </span>
        </div>

        <Button className={'mt-3'} danger disabled={!file} isLoading={isRestoring} onClick={handleRestore}>
          {translate('common.actions.restore')}
        </Button>

        {result && (
          <Alert className={'mt-4'}>
            <CheckCircle2 className={'h-4 w-4'} />

            <AlertTitle>{translate('settings.backups.restore.success')}</AlertTitle>

            <AlertDescription>
              <p>{translate('settings.backups.restore.migrationsApplied', { count: result.migrations.length })}</p>

              {result.migrations.length > 0 && (
                <ul className={'mt-2 list-disc space-y-0.5 pl-4 font-mono text-xs'}>
                  {result.migrations.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}
      </Section>
    </div>
  );
}
